import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import { AppDispatch, persistor, RootState } from '@/storage/store.ts'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { refreshToken } from '@/storage/user/authSlice.ts'
import { AuthService } from '../pages/authentication/AuthService'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'

const FIVE_MINUTES = 5 * 60 * 1000

export default function AuthSessionMonitor({ children }: ChildrenProp) {
  const navigateTo = useNavigate()
  const StoreDispatch = useDispatch<AppDispatch>()
  const AuthSessionState = useSelector((state: RootState) => state.auth)

  const handleLogout = useCallback(() => {
    AuthService.logout()
    persistor.purge()
    navigateTo(AppRoutePaths.auth.login)
  }, [StoreDispatch, navigateTo])

  const setupTokenRefresh = useCallback(() => {
    const now = Date.now()
    const timeToExpire = AuthSessionState.expires_at - now - FIVE_MINUTES

    if (timeToExpire > 0) {
      const timerID = window.setTimeout(() => {
        if (AUTH_MODE === 'keycloak') keycloakAuthService().ensureFreshToken(30)
        else StoreDispatch(refreshToken())
      }, timeToExpire)

      return () => clearTimeout(timerID)
    }
  }, [AuthSessionState.expires_at, AuthSessionState.rememberMeToken])

  useEffect(() => {
    if (!AuthSessionState.access_token) {
      if (!AuthSessionState.rememberMeToken) {
        handleLogout()
      }
      return
    }

    const cleanup = setupTokenRefresh()
    return cleanup
  }, [
    AuthSessionState.access_token,
    AuthSessionState.rememberMeToken,
    handleLogout,
    setupTokenRefresh,
  ])

  if (
    AuthSessionState.access_token == null &&
    !AuthSessionState.rememberMeToken
  ) {
    return <Navigate to={AppRoutePaths.auth.login} />
  }

  return <>{children}</>
}
